import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { Socket } from 'socket.io-client';

import { useAuth } from './AuthProvider';
import socketService, {
  type SocketEventMap,
  type TransactionCreatedEvent,
  type InventoryUpdatedEvent,
  type LowStockAlertEvent,
  type NotificationEvent,
} from '../lib/socket';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  reconnect: () => void;
  subscribe: <K extends keyof SocketEventMap>(
    event: K,
    callback: (data: SocketEventMap[K]) => void
  ) => () => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const { accessToken, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');

  const connect = useCallback(() => {
    if (!accessToken) return;

    setConnectionStatus('connecting');

    const s = socketService.connect(accessToken);

    // MVP: Handle case where WebSocket is disabled (returns null)
    if (!s) {
      setConnectionStatus('disconnected');
      return;
    }

    setSocket(s);

    s.on('connect', () => {
      setConnectionStatus('connected');
    });

    s.on('disconnect', () => {
      setConnectionStatus('disconnected');
    });

    s.on('connect_error', () => {
      setConnectionStatus('error');
    });
  }, [accessToken]);

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      connect();

      return () => {
        socketService.disconnect();
        setSocket(null);
        setConnectionStatus('disconnected');
      };
    }
  }, [isAuthenticated, accessToken, connect]);

  const reconnect = useCallback(() => {
    socketService.disconnect();
    setSocket(null);
    setConnectionStatus('disconnected');

    // Short delay before reconnecting
    setTimeout(() => {
      connect();
    }, 500);
  }, [connect]);

  const subscribe = useCallback(
    <K extends keyof SocketEventMap>(
      event: K,
      callback: (data: SocketEventMap[K]) => void
    ): (() => void) => {
      socketService.on(event, callback);

      return () => {
        socketService.off(event, callback);
      };
    },
    []
  );

  const value = useMemo<SocketContextValue>(
    () => ({
      socket,
      isConnected: connectionStatus === 'connected',
      connectionStatus,
      reconnect,
      subscribe,
    }),
    [socket, connectionStatus, reconnect, subscribe]
  );

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket(): SocketContextValue {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }

  return context;
}

// Specialized hooks for common socket events
export function useTransactionEvents(
  onCreated?: (data: TransactionCreatedEvent) => void,
  onUpdated?: (data: TransactionCreatedEvent) => void
): void {
  const { subscribe } = useSocket();

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (onCreated) {
      unsubscribes.push(subscribe('transaction:created', onCreated));
    }

    if (onUpdated) {
      unsubscribes.push(subscribe('transaction:updated', onUpdated));
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [subscribe, onCreated, onUpdated]);
}

export function useInventoryEvents(
  onUpdated?: (data: InventoryUpdatedEvent) => void,
  onLowStock?: (data: LowStockAlertEvent) => void
): void {
  const { subscribe } = useSocket();

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (onUpdated) {
      unsubscribes.push(subscribe('inventory:updated', onUpdated));
    }

    if (onLowStock) {
      unsubscribes.push(subscribe('inventory:low_stock', onLowStock));
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [subscribe, onUpdated, onLowStock]);
}

export function useNotificationEvents(
  onNotification?: (data: NotificationEvent) => void
): void {
  const { subscribe } = useSocket();

  useEffect(() => {
    if (!onNotification) return;

    return subscribe('notification:new', onNotification);
  }, [subscribe, onNotification]);
}
